import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

export type ColorTheme = 'default' | 'blue' | 'indigo' | 'purple' | 'pink' | 'red' | 'orange' | 'yellow' | 'green' | 'teal' | 'cyan' | 'chocolate' | 'brown' | 'grey' | 'black' | 'pista';
export type BackgroundGradient = 'bg-default' | 'bg-white' | 'bg-r-gradient' | 'bg-gradient-1' | 'bg-gradient-2' | 'bg-gradient-3' | 'bg-gradient-4' | 'bg-gradient-5' | 'bg-gradient-6' | 'bg-gradient-7' | 'bg-gradient-8' | 'bg-gradient-9' | 'bg-gradient-10';
export type HeaderLayout = 'default' | 'standard' | 'boxed';
export type HeaderStyle = 'transparent' | 'white' | 'bg' | 'theme' | 'accent';
export type SidebarLayout = 'default' | 'standard' | 'boxed' | 'iconic';
export type SidebarStyle = 'transparent' | 'white' | 'bg' | 'theme' | 'accent';
export type Direction = 'ltr' | 'rtl';
export type BackgroundImage = string;

interface ThemeSettings {
  colorTheme: ColorTheme;
  backgroundGradient: BackgroundGradient;
  darkMode: boolean;
  headerLayout: HeaderLayout;
  headerStyle: HeaderStyle;
  sidebarLayout: SidebarLayout;
  sidebarStyle: SidebarStyle;
  direction: Direction;
  backgroundImage: BackgroundImage;
}

interface ThemeContextType extends ThemeSettings {
  setColorTheme: (theme: ColorTheme) => void;
  setBackgroundGradient: (gradient: BackgroundGradient) => void;
  setDarkMode: (enabled: boolean) => void;
  setHeaderLayout: (layout: HeaderLayout) => void;
  setHeaderStyle: (style: HeaderStyle) => void;
  setSidebarLayout: (layout: SidebarLayout) => void;
  setSidebarStyle: (style: SidebarStyle) => void;
  setDirection: (dir: Direction) => void;
  setBackgroundImage: (image: BackgroundImage) => void;
}

const defaultSettings: ThemeSettings = {
  colorTheme: 'purple',
  backgroundGradient: 'bg-default',
  darkMode: false,
  headerLayout: 'standard',
  headerStyle: 'white',
  sidebarLayout: 'standard',
  sidebarStyle: 'white',
  direction: 'ltr',
  backgroundImage: 'bg-none',
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'adminuiux-theme-settings';

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<ThemeSettings>(() => {
    // Load from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return { ...defaultSettings, ...JSON.parse(stored) };
      } catch {
        return defaultSettings;
      }
    }
    return defaultSettings;
  });

  // Save to localStorage whenever settings change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  // Apply theme classes to body
  useEffect(() => {
    const body = document.body;

    // Remove all theme classes
    body.classList.remove(
      'theme-blue', 'theme-indigo', 'theme-purple', 'theme-pink', 'theme-red',
      'theme-orange', 'theme-yellow', 'theme-green', 'theme-teal', 'theme-cyan',
      'theme-chocolate', 'theme-brown', 'theme-grey', 'theme-black', 'theme-pista'
    );

    // Add color theme class
    if (settings.colorTheme !== 'default') {
      body.classList.add(`theme-${settings.colorTheme}`);
    }

    // Remove all background gradient classes
    body.classList.remove(
      'bg-default', 'bg-white', 'bg-r-gradient',
      'bg-gradient-1', 'bg-gradient-2', 'bg-gradient-3', 'bg-gradient-4', 'bg-gradient-5',
      'bg-gradient-6', 'bg-gradient-7', 'bg-gradient-8', 'bg-gradient-9', 'bg-gradient-10'
    );

    // Add background gradient class
    body.classList.add(settings.backgroundGradient);

    // Apply dark mode
    document.documentElement.setAttribute('data-bs-theme', settings.darkMode ? 'dark' : 'light');

    // Remove all header layout classes
    body.classList.remove('adminuiux-header-boxed');

    // Add header layout class
    if (settings.headerLayout === 'boxed') {
      body.classList.add('adminuiux-header-boxed');
    }

    // Remove all header style classes
    body.classList.remove(
      'adminuiux-header-transparent',
      'adminuiux-header-fill-white',
      'adminuiux-header-fill-bg',
      'adminuiux-header-fill-theme',
      'adminuiux-header-fill-accent'
    );

    // Add header style class
    if (settings.headerStyle !== 'transparent') {
      body.classList.add(`adminuiux-header-fill-${settings.headerStyle}`);
    }

    // Remove all sidebar layout classes
    body.classList.remove(
      'adminuiux-sidebar-boxed',
      'adminuiux-sidebar-iconic'
    );

    // Add sidebar layout class
    if (settings.sidebarLayout === 'boxed') {
      body.classList.add('adminuiux-sidebar-boxed');
    } else if (settings.sidebarLayout === 'iconic') {
      body.classList.add('adminuiux-sidebar-iconic');
    }

    // Remove all sidebar style classes
    body.classList.remove(
      'adminuiux-sidebar-fill-white',
      'adminuiux-sidebar-fill-bg',
      'adminuiux-sidebar-fill-theme',
      'adminuiux-sidebar-fill-accent'
    );

    // Add sidebar style class
    if (settings.sidebarStyle !== 'transparent') {
      body.classList.add(`adminuiux-sidebar-fill-${settings.sidebarStyle}`);
    }

    // Apply direction
    document.documentElement.setAttribute('dir', settings.direction);

    // Apply Background Image
    if (settings.backgroundImage === 'bg-none') {
      body.style.removeProperty('--adminuiux-main-bg');
    } else {
      body.style.setProperty('--adminuiux-main-bg', `url('${settings.backgroundImage}')`);
    }

  }, [settings]);

  const setColorTheme = (theme: ColorTheme) => {
    setSettings(prev => ({ ...prev, colorTheme: theme }));
  };

  const setBackgroundGradient = (gradient: BackgroundGradient) => {
    setSettings(prev => ({ ...prev, backgroundGradient: gradient }));
  };

  const setDarkMode = (enabled: boolean) => {
    setSettings(prev => ({ ...prev, darkMode: enabled }));
  };

  const setHeaderLayout = (layout: HeaderLayout) => {
    setSettings(prev => ({ ...prev, headerLayout: layout }));
  };

  const setHeaderStyle = (style: HeaderStyle) => {
    setSettings(prev => ({ ...prev, headerStyle: style }));
  };

  const setSidebarLayout = (layout: SidebarLayout) => {
    setSettings(prev => ({ ...prev, sidebarLayout: layout }));
  };

  const setSidebarStyle = (style: SidebarStyle) => {
    setSettings(prev => ({ ...prev, sidebarStyle: style }));
  };

  const setDirection = (dir: Direction) => {
    setSettings(prev => ({ ...prev, direction: dir }));
  };

  const setBackgroundImage = (image: BackgroundImage) => {
    setSettings(prev => ({ ...prev, backgroundImage: image }));
  };

  return (
    <ThemeContext.Provider
      value={{
        ...settings,
        setColorTheme,
        setBackgroundGradient,
        setDarkMode,
        setHeaderLayout,
        setHeaderStyle,
        setSidebarLayout,
        setSidebarStyle,
        setDirection,
        setBackgroundImage,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
